import * as React from "react";
import type { LucideIcon } from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

export interface StatCardProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  /** Short metric label (muted). */
  label: React.ReactNode;
  /** The metric value. Rendered with tabular-nums so figures align. */
  value: React.ReactNode;
  /** Optional Lucide icon shown in a leading chip. */
  icon?: LucideIcon;
  /**
   * Semantic accent for the icon chip. Only `warning` (amber) is sanctioned —
   * everything else stays neutral so a wall of stat cards reads calm.
   */
  accent?: "neutral" | "warning";
}

const ACCENT_CHIP = {
  neutral: "bg-muted text-muted-foreground",
  warning: "bg-amber-50 text-amber-700",
} as const;

/**
 * Neutral summary metric card (label + value, optional leading icon). Mirrors
 * the Consumables summary cards. Keep the palette calm: reserve the `warning`
 * accent for a genuine attention state (e.g. low stock), never as decoration.
 */
export function StatCard({
  label,
  value,
  icon: Icon,
  accent = "neutral",
  className,
  ...props
}: StatCardProps) {
  return (
    <Card className={className} {...props}>
      <CardContent className="p-4 flex items-center gap-3">
        {Icon ? (
          <div className={cn("rounded-md p-2", ACCENT_CHIP[accent])}>
            <Icon className="h-5 w-5" aria-hidden="true" />
          </div>
        ) : null}
        <div className="min-w-0">
          <p className="text-sm text-muted-foreground">{label}</p>
          <p className="text-xl font-semibold tabular-nums">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}
